import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, Alert, Container, CircularProgress, keyframes, FormControlLabel, Checkbox } from '@mui/material';
import { ShieldCheckIcon } from 'lucide-react';
import { createAuditLog } from '../../utils/auditLog';
import { generateDeviceFingerprint } from '../../utils/deviceFingerprint';
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;
const OtpVerificationPage: React.FC = () => {
  const [otp, setOtp] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const userData = location.state?.userData;
  useEffect(() => {
    if (!userData) {
      navigate('/login');
    }
  }, [userData, navigate]);
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp !== 'Test01') {
      setError('Invalid OTP. Please try again.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    setIsLoading(true);
    try {
      // Store user session
      localStorage.setItem('user', JSON.stringify(userData));
      // Handle trusted device - save metadata against user
      if (trustDevice) {
        const deviceFingerprint = generateDeviceFingerprint();
        const trustedDevices = JSON.parse(localStorage.getItem('trustedDevices') || '{}');
        if (!trustedDevices[userData.id]) {
          trustedDevices[userData.id] = [];
        }
        // Save comprehensive device metadata
        const deviceMetadata = {
          fingerprint: deviceFingerprint,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screenResolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          deviceName: `${navigator.platform} - ${navigator.userAgent.split('(')[1]?.split(')')[0] || 'Unknown'}`
        };
        trustedDevices[userData.id].push(deviceMetadata);
        localStorage.setItem('trustedDevices', JSON.stringify(trustedDevices));
        // Also save to user's record in users collection
        const storedUsersData = localStorage.getItem('users');
        if (storedUsersData) {
          const users = JSON.parse(storedUsersData);
          const updatedUsers = users.map((user: any) => {
            if (user.id === userData.id) {
              return {
                ...user,
                trustedDevices: trustedDevices[userData.id]
              };
            }
            return user;
          });
          localStorage.setItem('users', JSON.stringify(updatedUsers));
        }
        createAuditLog({
          userId: userData.id,
          eventType: 'Status Change',
          source: 'SYSTEM',
          changes: [{
            fieldName: 'Trusted Device',
            oldValue: null,
            newValue: `Device added: ${deviceMetadata.deviceName}`
          }]
        });
      }
      // ROUTING LOGIC AFTER OTP VERIFICATION
      if (userData.firstLoginRequired) {
        // First time login - go to password change
        navigate('/force-password-change', {
          state: {
            userData,
            trustDevice
          }
        });
      } else if (!trustDevice) {
        // Not first time login, but device is NOT trusted - force password change
        navigate('/force-password-change', {
          state: {
            userData,
            trustDevice: false
          }
        });
      } else {
        // Not first time login and device IS trusted - go to dashboard
        if (userData.role === 'checker') {
          navigate('/transaction-approval');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <Box sx={{
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    position: 'relative',
    px: 2,
    overflow: 'hidden'
  }} data-id="element-222">
      <Container maxWidth="sm" sx={{
      mb: 4,
      mt: 4,
      position: 'relative',
      zIndex: 1
    }} data-id="element-223">
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3
      }} data-id="element-224">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xyh4FinweYvNRM6XVnTFSv/openRemitLogo-1.png" alt="OpenRemit Logo" style={{
          height: 80,
          marginBottom: 20
        }} data-id="element-225" />
          <Typography variant="subtitle1" sx={{
          color: '#6B7280',
          textAlign: 'center',
          fontWeight: 600,
          letterSpacing: '1px'
        }} data-id="element-226">
            Security Verification
          </Typography>
        </Box>

        <Paper elevation={0} sx={{
        p: {
          xs: 3,
          sm: 4
        },
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
        position: 'relative',
        animation: isShaking ? `${shakeAnimation} 0.5s ease-in-out` : 'none'
      }} data-id="element-227">
          <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 2
        }} data-id="element-228">
            <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#E8F0FE',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }} data-id="element-229">
              <ShieldCheckIcon size={32} color="#0052CC" data-id="element-230" />
            </Box>
          </Box>

          <Typography variant="h5" component="h2" sx={{
          mb: 1,
          textAlign: 'center',
          color: '#1F2937',
          fontWeight: 700
        }} data-id="element-231">
            Verify OTP
          </Typography>

          <Typography variant="body2" sx={{
          mb: 3,
          textAlign: 'center',
          color: 'text.secondary'
        }} data-id="element-232">
            Enter the alphanumeric code sent to your email
          </Typography>

          {error && <Alert severity="error" sx={{
          mb: 3,
          borderRadius: '12px'
        }} data-id="element-233">
              {error}
            </Alert>}

          <Alert severity="info" sx={{
          mb: 3
        }} data-id="element-234">
            <Typography variant="caption" data-id="element-235">
              Demo OTP: <strong data-id="element-236">Test01</strong>
            </Typography>
          </Alert>

          <form onSubmit={handleVerifyOtp} data-id="element-237">
            <TextField id="otp" label="OTP Code" variant="outlined" fullWidth value={otp} onChange={e => setOtp(e.target.value)} error={!!error} inputProps={{
            maxLength: 6,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              letterSpacing: '0.3rem'
            }
          }} sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(0, 180, 216, 0.15)'
              }
            }
          }} data-id="element-238" />

            <FormControlLabel control={<Checkbox checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} sx={{
            color: '#0077B6',
            '&.Mui-checked': {
              color: '#0077B6'
            }
          }} data-id="element-240" />} label={<Box data-id="element-241">
                  <Typography variant="body2" fontWeight={500} data-id="element-242">
                    Trust this device
                  </Typography>
                  <Typography variant="caption" color="text.secondary" data-id="element-243">
                    Skip OTP verification on this device for future logins
                  </Typography>
                </Box>} sx={{
            mb: 3
          }} data-id="element-239" />

            <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading || otp.length < 6} sx={{
            py: 1.5,
            borderRadius: '6px',
            backgroundColor: '#0052CC',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#003DA8',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
            }
          }} data-id="element-244">
              {isLoading ? <CircularProgress size={24} sx={{
              color: 'white'
            }} data-id="element-245" /> : 'Verify OTP'}
            </Button>

            <Button variant="text" fullWidth onClick={() => navigate('/login')} sx={{
            color: '#0077B6',
            fontWeight: 500
          }} data-id="element-246">
              Back to Login
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>;
};
export default OtpVerificationPage;